import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  IconButton,
  Typography,
  makeStyles,
  Box,
} from "@material-ui/core";
import { Alert, AlertTitle } from "@material-ui/lab";
import CloseIcon from "@material-ui/icons/Close";
import SendIcon from "@material-ui/icons/Send";
import { toast } from "react-toastify";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { getStandardButtonStyles } from "../../styles/standardButtons";

const useStyles = makeStyles((theme) => ({
  ...getStandardButtonStyles(theme),
  dialogPaper: {
    backgroundColor: theme.palette.type === "dark" ? "#1E2126" : "#FFFFFF",
    borderRadius: 16,
    border: `1px solid ${theme.palette.primary.main}40`,
    boxShadow: "0 16px 48px rgba(0, 0, 0, 0.45)",
    overflow: "hidden",
  },
  dialogHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2, 2.5),
    borderBottom: `1px solid ${
      theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"
    }`,
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  headerIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 40,
    height: 40,
    borderRadius: 10,
    backgroundColor: `${theme.palette.primary.main}1A`,
    color: theme.palette.primary.main,
  },
  headerBadge: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 1.1,
    color: theme.palette.primary.main,
    marginBottom: 2,
  },
  headerTitle: {
    fontSize: "1.05rem",
    fontWeight: 600,
    color: theme.palette.type === "dark" ? "#FFFFFF" : theme.palette.text.primary,
  },
  closeButton: {
    padding: 6,
    color: theme.palette.text.secondary,
    "&:hover": {
      color: theme.palette.text.primary,
      backgroundColor: "transparent",
    },
  },
  contentArea: {
    padding: theme.spacing(2.5, 3),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2.5),
  },
  sectionBox: {
    padding: theme.spacing(2),
    borderRadius: 12,
    border: `1px solid ${
      theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"
    }`,
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.02)" : "rgba(0, 0, 0, 0.02)",
  },
  sectionTitle: {
    fontSize: "0.875rem",
    fontWeight: 600,
    color: theme.palette.type === "dark" ? "#FFFFFF" : theme.palette.text.primary,
    marginBottom: 4,
  },
  sectionHint: {
    fontSize: "0.78rem",
    color: theme.palette.text.secondary,
    marginBottom: 12,
  },
  filterRow: {
    display: "flex",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
    marginTop: 4,
  },
  filterControl: {
    flex: 1,
    minWidth: 180,
  },
  dialogActions: {
    padding: theme.spacing(2, 2.5),
    borderTop: `1px solid ${
      theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"
    }`,
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1.5),
  },
  dividerSection: {
    borderTop: `1px solid ${
      theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"
    }`,
    paddingTop: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
  keywordInputs: {
    display: "flex",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
    },
  },
}));

const CreateGroupCampaignModal = ({ open, onClose, groupContact }) => {
  const classes = useStyles();

  const [listName, setListName] = useState("");
  const [baseMessage, setBaseMessage] = useState("");
  const [whatsappId, setWhatsappId] = useState("");
  const [whatsapps, setWhatsapps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [useAi, setUseAi] = useState(false);
  const [useButtons, setUseButtons] = useState(true);
  const [button1, setButton1] = useState("");
  const [button2, setButton2] = useState("");

  // [DESTINO] Clasificación opcional: Embudo -> Etapa -> Etiqueta
  const [kanbanBoards, setKanbanBoards] = useState([]);
  const [destBoardId, setDestBoardId] = useState("");
  const [destLaneId, setDestLaneId] = useState("");
  const [destTagId, setDestTagId] = useState("");

  const [membershipStatus, setMembershipStatus] = useState({
    checking: false,
    isMember: null,
    groupName: "",
    reason: ""
  });

  const checkMembership = async (targetWhatsappId) => {
    if (!targetWhatsappId || !groupContact?.id) {
      setMembershipStatus({ checking: false, isMember: null, groupName: "", reason: "" });
      return;
    }
    setMembershipStatus(prev => ({ ...prev, checking: true }));
    try {
      const { data } = await api.get(`/contacts/${groupContact.id}/check-group-member`, {
        params: { whatsappId: targetWhatsappId }
      });
      setMembershipStatus({
        checking: false,
        isMember: data.isMember,
        groupName: data.groupName || groupContact?.name || "",
        reason: data.reason || ""
      });
    } catch (err) {
      console.error("Error checking group membership:", err);
      setMembershipStatus({
        checking: false,
        isMember: false,
        groupName: groupContact?.name || "",
        reason: "ERROR"
      });
    }
  };

  const boardHasWhatsapp = (board, wid) =>
    (board?.whatsapps || []).some(w => String(w.id) === String(wid)) ||
    String(board?.whatsappId) === String(wid);

  useEffect(() => {
    if (open) {
      setListName(i18n.t("campaigns.groupInviteModal.listNameDefault", { name: groupContact?.name || "" }));
      setBaseMessage("");
      setWhatsappId("");
      setUseAi(false);
      setUseButtons(true);
      setButton1(i18n.t("campaigns.groupInviteModal.defaultAcceptWord", "SÍ, QUIERO UNIRME"));
      setButton2(i18n.t("campaigns.groupInviteModal.defaultRejectWord", "NO, GRACIAS"));
      setDestBoardId("");
      setDestLaneId("");
      setDestTagId("");
      loadWhatsapps();
      loadBoards();
    }
  }, [open, groupContact]);

  const loadWhatsapps = async () => {
    try {
      const { data } = await api.get("/whatsapp");
      const activeWhatsapps = data.filter(w => w.status === "CONNECTED" && (!w.channel || w.channel === "whatsapp"));
      setWhatsapps(activeWhatsapps);
      const groupOrigWa = activeWhatsapps.find(w => String(w.id) === String(groupContact?.whatsappId));
      const initialWaId = groupOrigWa ? groupOrigWa.id : (activeWhatsapps[0]?.id || "");
      if (initialWaId) {
        setWhatsappId(initialWaId);
        checkMembership(initialWaId);
      }
    } catch (err) {
      toast.error(i18n.t("campaigns.groupInviteModal.errorConnection", "Seleccione una conexión de WhatsApp"));
    }
  };

  const loadBoards = async () => {
    try {
      const { data } = await api.get("/kanban-boards");
      setKanbanBoards(Array.isArray(data) ? data : []);
    } catch (err) {
      setKanbanBoards([]);
    }
  };

  // Embudos que tienen asignada la conexión elegida
  const availableBoards = kanbanBoards.filter(kb => !whatsappId || boardHasWhatsapp(kb, whatsappId));
  const selectedBoard = kanbanBoards.find(kb => String(kb.id) === String(destBoardId));
  const lanes = selectedBoard?.tags || [];
  const laneTags = (lanes.find(l => String(l.id) === String(destLaneId))?.kanbanLaneTags || []).map(k => k.tag).filter(Boolean);

  const handleChangeConnection = (value) => {
    setWhatsappId(value);
    checkMembership(value);
    if (selectedBoard && !boardHasWhatsapp(selectedBoard, value)) {
      setDestBoardId("");
      setDestLaneId("");
      setDestTagId("");
    }
  };

  const handleSave = async () => {
    if (!whatsappId) {
      toast.error(i18n.t("campaigns.groupInviteModal.errorConnection", "Seleccione una conexión de WhatsApp"));
      return;
    }
    if (membershipStatus.isMember === false) {
      toast.error(i18n.t("campaigns.groupInviteModal.notMemberAlertTitle", "Conexión no autorizada para este grupo"));
      return;
    }
    if (!baseMessage.trim()) {
      toast.error(i18n.t("campaigns.groupInviteModal.errorMessage", "El texto del mensaje es obligatorio"));
      return;
    }

    setLoading(true);
    try {
      await api.post("/campaigns/group-invite", {
        groupId: groupContact.id,
        listName,
        whatsappId,
        baseMessage,
        useAi,
        useButtons,
        button1: button1 || i18n.t("campaigns.groupInviteModal.defaultAcceptWord", "SÍ, QUIERO UNIRME"),
        button2: button2 || i18n.t("campaigns.groupInviteModal.defaultRejectWord", "NO, GRACIAS"),
        destinationKanbanBoardId: destBoardId || null,
        moveToLaneId: destLaneId || null,
        kanbanTagId: destTagId || null
      });
      toast.success(i18n.t("campaigns.groupInviteModal.successMessage", "Campaña y Lista generadas con éxito. La IA procesó las variaciones."));
      onClose();
    } catch (err) {
      const backendMsg = err?.response?.data?.error;
      toast.error(backendMsg && i18n.exists(`backendErrors.${backendMsg}`) ? i18n.t(`backendErrors.${backendMsg}`) : (backendMsg || "Error"));
    }
    setLoading(false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ className: classes.dialogPaper }}
    >
      <div className={classes.dialogHeader}>
        <div className={classes.headerLeft}>
          <div className={classes.headerIcon}>
            <SendIcon fontSize="small" />
          </div>
          <div>
            <Typography className={classes.headerBadge}>
              {i18n.t("campaigns.groupInviteModal.badge", "BotMix | Campaña para Grupo")}
            </Typography>
            <Typography className={classes.headerTitle}>
              {i18n.t("campaigns.groupInviteModal.title", { name: groupContact?.name || "" })}
            </Typography>
          </div>
        </div>
        <IconButton
          className={classes.closeButton}
          onClick={onClose}
          size="small"
          aria-label="close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      <DialogContent className={classes.contentArea}>
        <FormControl variant="outlined" fullWidth size="small">
          <InputLabel>
            {i18n.t("campaigns.groupInviteModal.connectionSelectLabel", "Conexión a utilizar")}
          </InputLabel>
          <Select
            value={whatsappId}
            onChange={(e) => handleChangeConnection(e.target.value)}
            label={i18n.t("campaigns.groupInviteModal.connectionSelectLabel", "Conexión a utilizar")}
          >
            {whatsapps.map((w) => (
              <MenuItem key={w.id} value={w.id}>
                {w.name} ({w.number})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {membershipStatus.checking && (
          <Box display="flex" alignItems="center" gap={1} mt={1}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="textSecondary">
              {i18n.t("campaigns.groupInviteModal.checkingMembership", "Verificando membresía en el grupo...")}
            </Typography>
          </Box>
        )}

        {!membershipStatus.checking && membershipStatus.isMember === false && (
          <Alert
            severity="error"
            style={{ marginTop: 12, borderRadius: 8 }}
          >
            <AlertTitle><strong>{i18n.t("campaigns.groupInviteModal.notMemberAlertTitle", "⛔ Conexión no autorizada")}</strong></AlertTitle>
            <Typography variant="body2">
              {i18n.t("campaigns.groupInviteModal.notMemberAlertDesc", "Este número no pertenece al grupo de WhatsApp. Para poder enviarle mensajes a los miembros de este grupo desde este número, primero debes agregarlo como participante en el grupo.")}
            </Typography>
          </Alert>
        )}

        {!membershipStatus.checking && membershipStatus.isMember === true && (
          <Alert
            severity="success"
            style={{ marginTop: 12, borderRadius: 8, backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534' }}
          >
            <Typography variant="body2" style={{ fontWeight: 600 }}>
              {i18n.t("campaigns.groupInviteModal.memberVerified", "✅ Conexión verificada: Este número es participante del grupo y puede enviar invitaciones de forma segura.")}
            </Typography>
          </Alert>
        )}

        {/* Badge Informativo de Protección Anti-Baneo */}
        <Box mt={1} p={1} borderRadius={6} style={{ backgroundColor: 'rgba(59, 130, 246, 0.08)', border: '1px solid rgba(59, 130, 246, 0.2)' }}>
          <Typography variant="caption" style={{ color: '#2563eb', fontWeight: 600, display: 'block' }}>
            {i18n.t("campaigns.groupInviteModal.antiBanBadge", "🛡️ Protección Anti-Baneo Activa: Esta campaña de grupo enviará un máximo seguro de 30 contactos/día entre las 09:00 y las 18:00 con pausas humanas.")}
          </Typography>
        </Box>

        {/* Clasificación de destino */}
        <div className={classes.sectionBox}>
          <Typography className={classes.sectionTitle}>
            {i18n.t("campaigns.groupInviteModal.destTitle", "Clasificación de destino (opcional)")}
          </Typography>
          <Typography className={classes.sectionHint}>
            {i18n.t("campaigns.groupInviteModal.destHint", "Si no eliges embudo, los contactos quedan solo con la Marca y la Conexión.")}
          </Typography>
          <div className={classes.filterRow}>
            <FormControl variant="outlined" size="small" className={classes.filterControl}>
              <InputLabel>{i18n.t("campaigns.dialog.form.destFunnel", "Embudo Destino")}</InputLabel>
              <Select
                value={destBoardId}
                label={i18n.t("campaigns.dialog.form.destFunnel", "Embudo Destino")}
                onChange={(e) => {
                  setDestBoardId(e.target.value);
                  setDestLaneId("");
                  setDestTagId("");
                }}
              >
                <MenuItem value="">
                  <em>{i18n.t("campaigns.dialog.form.noFunnel", "Sin embudo")}</em>
                </MenuItem>
                {availableBoards.map((kb) => (
                  <MenuItem key={kb.id} value={kb.id}>
                    {kb.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              variant="outlined"
              size="small"
              className={classes.filterControl}
              disabled={!destBoardId}
            >
              <InputLabel>{i18n.t("campaigns.dialog.form.destStage", "Mover a la Etapa")}</InputLabel>
              <Select
                value={destLaneId}
                label={i18n.t("campaigns.dialog.form.destStage", "Mover a la Etapa")}
                onChange={(e) => {
                  setDestLaneId(e.target.value);
                  setDestTagId("");
                }}
              >
                <MenuItem value="">
                  <em>{i18n.t("campaigns.kanbanSegment.noMove", "Sin cambios (No mover)")}</em>
                </MenuItem>
                {lanes.map((l) => (
                  <MenuItem key={l.id} value={l.id}>
                    {l.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              variant="outlined"
              size="small"
              className={classes.filterControl}
              disabled={!destLaneId}
            >
              <InputLabel>{i18n.t("campaigns.kanbanSegment.addTags", "Agregar Etiqueta")}</InputLabel>
              <Select
                value={destTagId}
                label={i18n.t("campaigns.kanbanSegment.addTags", "Agregar Etiqueta")}
                onChange={(e) => setDestTagId(e.target.value)}
              >
                <MenuItem value="">
                  <em>{i18n.t("campaigns.kanbanSegment.noTags", "Sin cambios (No agregar etiqueta)")}</em>
                </MenuItem>
                {laneTags.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </div>

        {/* Mensaje Base */}
        <TextField
          label={i18n.t("campaigns.groupInviteModal.baseMessageLabel", "Mensaje Base")}
          variant="outlined"
          fullWidth
          multiline
          rows={5}
          value={baseMessage}
          onChange={(e) => setBaseMessage(e.target.value)}
          helperText={i18n.t(
            "campaigns.groupInviteModal.baseMessageHelper",
            "El sistema añadirá automáticamente un saludo y despedida dinámicos (Spintax) para evitar bloqueos por spam. No los incluya aquí."
          )}
        />

        {/* Switch IA */}
        <FormControlLabel
          control={
            <Switch
              checked={useAi}
              onChange={(e) => setUseAi(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Typography variant="body2" style={{ fontWeight: 500 }}>
              {i18n.t(
                "campaigns.groupInviteModal.useAiLabel",
                "Utilizar Inteligencia Artificial para variaciones del mensaje (Consume Saldo)"
              )}
            </Typography>
          }
        />

        {/* Sección de Confirmación Anti-Ban */}
        <div className={classes.dividerSection}>
          <FormControlLabel
            control={
              <Switch
                checked={useButtons}
                onChange={(e) => setUseButtons(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="body2" style={{ fontWeight: 600 }}>
                {i18n.t(
                  "campaigns.groupInviteModal.useKeywordsLabel",
                  "Solicitar confirmación mediante Palabra Clave (Texto Seguro Anti-Ban)"
                )}
              </Typography>
            }
          />
          {useButtons && (
            <div className={classes.keywordInputs}>
              <TextField
                label={i18n.t("campaigns.groupInviteModal.acceptWordLabel", "Palabra de Aceptación")}
                variant="outlined"
                size="small"
                fullWidth
                value={button1}
                onChange={(e) => setButton1(e.target.value)}
                helperText={i18n.t(
                  "campaigns.groupInviteModal.acceptWordHelper",
                  "Ej: SÍ, ME INTERESA (Debes configurar esta misma frase en FlowBuilder)"
                )}
              />
              <TextField
                label={i18n.t("campaigns.groupInviteModal.rejectWordLabel", "Palabra de Rechazo")}
                variant="outlined"
                size="small"
                fullWidth
                value={button2}
                onChange={(e) => setButton2(e.target.value)}
                helperText={i18n.t("campaigns.groupInviteModal.rejectWordHelper", "Ej: NO, GRACIAS")}
              />
            </div>
          )}
        </div>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button
          onClick={onClose}
          disabled={loading}
          className={classes.secondaryButton}
        >
          {i18n.t("campaigns.groupInviteModal.buttonCancel", "Cancelar")}
        </Button>
        <Button
          onClick={handleSave}
          disabled={loading || membershipStatus.checking || membershipStatus.isMember === false}
          className={classes.applyButton}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            i18n.t("campaigns.groupInviteModal.buttonGenerate", "Generar Campaña")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateGroupCampaignModal;
